/**
 * Tag remediation campaigns.
 *
 * Replaces "one ticket per untagged resource" with one ticket per
 * (account/subscription/project) x (missing tag key). On a real estate that is
 * the difference between a queue somebody works and a queue nobody opens.
 *
 * The ordering is deliberate: scopes come back ranked by untagged SPEND, not
 * by resource count, because the number that gets a remediation prioritised is
 * "$47k/month we cannot allocate", not "31 rows". When a tenant has no
 * per-resource cost attribution the API says so and we show a dash - never a
 * zero, which would read as "this costs nothing" rather than "we don't know".
 */
import { useEffect, useState } from 'react';
import { ChevronRight, ChevronDown, ExternalLink, Copy, Check, AlertCircle } from 'lucide-react';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';

interface ScopeTicket {
  key: string | null;
  url: string | null;
  status: string;
}

interface RemediationScope {
  key: string;
  provider: string;
  scopeId: string;
  scopeType: string;
  scopeLabel: string;
  tagKey: string;
  resourceCount: number;
  untaggedMonthlyCost: number | null;
  resourceTypes: Array<{ type: string; count: number }>;
  regions: string[];
  ticket: ScopeTicket | null;
}

interface Props {
  apiBase: string;
  authHeaders: () => Record<string, string>;
  providerFilter: string;
}

const providerVariant = (p: string) =>
  p === 'aws' ? 'warning' : p === 'azure' ? 'info' : p === 'gcp' ? 'success' : 'muted';

const money = (v: number | null) =>
  v === null ? '—' : `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

export function TagRemediationTab({ apiBase, authHeaders, providerFilter }: Props) {
  const [scopes, setScopes] = useState<RemediationScope[]>([]);
  const [costAttribution, setCostAttribution] = useState<'available' | 'unavailable'>('unavailable');
  const [distinctResources, setDistinctResources] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [detail, setDetail] = useState<Record<string, { resources: any[]; script: string }>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = providerFilter && providerFilter !== 'all' ? `?provider=${providerFilter}` : '';
      const res = await fetch(`${apiBase}/tagging/remediation-scopes${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setScopes(body.data?.scopes || []);
      setCostAttribution(body.data?.costAttribution || 'unavailable');
      setDistinctResources(body.data?.distinctResources || 0);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerFilter]);

  const toggle = async (s: RemediationScope) => {
    if (expanded === s.key) {
      setExpanded(null);
      return;
    }
    setExpanded(s.key);
    if (detail[s.key]) return;
    try {
      const res = await fetch(
        `${apiBase}/tagging/remediation-scopes/${s.provider}/${encodeURIComponent(s.scopeId)}/${encodeURIComponent(s.tagKey)}/resources`,
        { headers: authHeaders() }
      );
      const body = await res.json();
      setDetail((d) => ({ ...d, [s.key]: { resources: body.data?.resources || [], script: body.data?.script || '' } }));
    } catch {
      setDetail((d) => ({ ...d, [s.key]: { resources: [], script: '' } }));
    }
  };

  const raiseTicket = async (s: RemediationScope) => {
    setBusy(s.key);
    try {
      const res = await fetch(
        `${apiBase}/tagging/remediation-scopes/${s.provider}/${encodeURIComponent(s.scopeId)}/${encodeURIComponent(s.tagKey)}/ticket`,
        { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' }, body: '{}' }
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.errors?.[0]?.message || `HTTP ${res.status}`);
      const t = body.data;
      setScopes((prev) =>
        prev.map((x) =>
          x.key === s.key ? { ...x, ticket: { key: t.external_key, url: t.external_url, status: t.status } } : x
        )
      );
    } catch (e: any) {
      setError(`Could not raise ticket: ${e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const copyScript = (key: string, script: string) => {
    navigator.clipboard.writeText(script);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) return <Card className="p-6 text-sm text-muted-foreground">Grouping untagged resources…</Card>;
  if (error)
    return (
      <Card className="p-6">
        <div className="flex items-start gap-2 text-sm text-rose-600">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      </Card>
    );

  const totalResources = scopes.reduce((n, s) => n + s.resourceCount, 0);
  const totalCost = scopes.reduce((n, s) => n + (s.untaggedMonthlyCost || 0), 0);
  const withTickets = scopes.filter((s) => s.ticket).length;

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <div className="text-xs text-muted-foreground">Remediation scopes</div>
            <div className="text-2xl font-semibold">{scopes.length}</div>
            <div className="text-xs text-muted-foreground">one ticket each</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Resource-tag gaps</div>
            <div className="text-2xl font-semibold">{totalResources}</div>
            {/* NOT the old ticket count. One resource missing three tags is
                three gaps but was only ever one per-resource ticket, so
                labelling this "tickets under the old model" overstated the
                saving. The honest comparison is scopes vs distinct resources. */}
            <div className="text-xs text-muted-foreground">across {distinctResources} resources</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Unallocatable spend</div>
            <div className="text-2xl font-semibold">
              {costAttribution === 'available' ? money(Math.round(totalCost)) : '—'}
            </div>
            <div className="text-xs text-muted-foreground">
              {costAttribution === 'available' ? 'per month' : 'not yet attributable'}
            </div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Ticketed</div>
            <div className="text-2xl font-semibold">{withTickets}</div>
            <div className="text-xs text-muted-foreground">of {scopes.length} scopes</div>
          </div>
        </div>
      </Card>

      {costAttribution === 'unavailable' && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          Per-resource cost attribution isn't available for this tenant, so scopes are ranked by resource
          count rather than unallocatable spend, and the cost column is hidden. It is not zero — it is
          unknown, and showing $0 would read as "this costs nothing".
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium">Scope</th>
              <th className="px-4 py-2.5 text-left font-medium">Missing tag</th>
              <th className="px-4 py-2.5 text-right font-medium">Resources</th>
              {/* Hidden entirely when this tenant has no cost attribution. A
                  column of dashes next to the (also dashed) Ticket column read
                  as two mystery blanks per row - the fix is to not draw a
                  column that can never hold a value, and let the banner above
                  explain why it is absent. */}
              {costAttribution === 'available' && (
                <th className="px-4 py-2.5 text-right font-medium">Unallocatable / mo</th>
              )}
              <th className="px-4 py-2.5 text-left font-medium">Ticket</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {scopes.map((s) => {
              const open = expanded === s.key;
              const d = detail[s.key];
              return (
                <>
                  <tr
                    key={s.key}
                    className="border-t border-border hover:bg-muted/30 cursor-pointer"
                    onClick={() => toggle(s)}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        <Badge variant={providerVariant(s.provider) as any}>{s.provider.toUpperCase()}</Badge>
                        <span className="font-medium">{s.scopeLabel}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="warning">{s.tagKey}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{s.resourceCount}</td>
                    {costAttribution === 'available' && (
                      <td className="px-4 py-2.5 text-right tabular-nums">{money(s.untaggedMonthlyCost)}</td>
                    )}
                    <td className="px-4 py-2.5">
                      {s.ticket ? (
                        s.ticket.url ? (
                          <a
                            href={s.ticket.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-primary"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {s.ticket.key || 'open'} <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">{s.ticket.status}</span>
                        )
                      ) : (
                        <span className="text-xs text-muted-foreground">Not raised</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {!s.ticket && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy === s.key}
                          onClick={(e: any) => {
                            e.stopPropagation();
                            raiseTicket(s);
                          }}
                        >
                          {busy === s.key ? 'Raising…' : 'Raise Ticket'}
                        </Button>
                      )}
                    </td>
                  </tr>

                  {open && (
                    <tr key={`${s.key}-detail`} className="border-t border-border bg-muted/20">
                      <td colSpan={costAttribution === 'available' ? 6 : 5} className="px-4 py-3">
                        <div className="mb-3 flex flex-wrap gap-2">
                          {s.resourceTypes.map((t) => (
                            <Badge key={t.type} variant="muted">
                              {t.type}: {t.count}
                            </Badge>
                          ))}
                        </div>

                        {!d && <div className="text-xs text-muted-foreground">Loading resources…</div>}

                        {d && (
                          <>
                            <div className="max-h-56 overflow-y-auto rounded border border-border bg-background">
                              <table className="w-full text-xs">
                                <tbody>
                                  {d.resources.map((r: any, i: number) => (
                                    <tr key={i} className="border-b border-border last:border-0">
                                      <td className="px-3 py-1.5 font-mono break-all">{r.resourceId}</td>
                                      <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{r.region}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {d.script && (
                              <div className="mt-3">
                                <div className="mb-1 flex items-center justify-between">
                                  <span className="text-xs font-medium text-muted-foreground">
                                    Remediation command
                                  </span>
                                  <button
                                    className="inline-flex items-center gap-1 text-xs text-primary"
                                    onClick={() => copyScript(s.key, d.script)}
                                  >
                                    {copied === s.key ? (
                                      <>
                                        <Check className="h-3 w-3" /> Copied
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="h-3 w-3" /> Copy
                                      </>
                                    )}
                                  </button>
                                </div>
                                <pre className="max-h-48 overflow-auto rounded border border-border bg-background p-2 text-[11px] leading-relaxed">
                                  {d.script}
                                </pre>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
